/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { getDataIdGenerator } from '../../../../core/utils/generateDataId';
import { HdTimeFormat } from '../../../../date-time/components/calendar-format/HdCalendar';
import {
  HdRadioGroup,
  HdRadio,
  HdMenu,
  HdMenuItem,
  HdIcon,
  HdIconButton
} from '../../../../components';
import {
  AuthorisedAccount,
  AuthorizationAccount,
  ServiceAccount
} from '../../../connector-form/interfaces/auth';
import styles from './AuthAccountBox.module.scss';
import { ConnectorAuthTypeEnum } from '../../../connector-form/interfaces/authType';
import { AuthAccountEnvironment } from '../auth-environment-selection-dialog/interface';
import { NewAuthTrigger } from '../new-auth-trigger/NewAuthTrigger';
import AuthAPI from '../../../../auth/api/AuthAPI';
import { EditServiceAccountDialog } from '../../../service-account-auth-select/components/edit-service-account-dialog/EditServiceAccountDialog';
import useAnalyticsTracker from '../../../../analytics/hooks/useAnalyticsTracker';
import {
  TRACKER_AUTH_ACCOUNT_CLICK,
  TRACKER_DISABLED_DRIVE_CREATE_REAUTHORISE_CLICK
} from '../../../connector-form/tracking/connectorFormTracking';
import { AccountEntityTypes } from '../../interfaces';
import { ConfirmDialogV1 } from '../../../../modal/components/confirm-dialog-v1/ConfirmDialogV1';
import useToaster from '../../../../toaster/hooks/useToaster';

export interface IAuthAccountsBoxProps {
  provider: string;
  authorisedAccounts: (ServiceAccount | AuthorisedAccount)[];
  selectedAccountId?: number;
  onNewAccountClick: Function;
  onDeleteAccount: (accountId) => void;
  entityType: AccountEntityTypes;
  connectorType: string;
  isProviderTypeGoogle: boolean;
  onAccountChange: (accountId: number) => void;
  accountEnvironmentLabelFn?: (env: AuthAccountEnvironment) => string;
  authType: ConnectorAuthTypeEnum;
}

export const generateDataId = getDataIdGenerator('auth-selection-box');

export function AuthAccountsBox({
  authorisedAccounts = [],
  selectedAccountId,
  isProviderTypeGoogle = false,
  authType,
  entityType,
  connectorType,
  onDeleteAccount,
  onNewAccountClick,
  provider,
  onAccountChange,
  accountEnvironmentLabelFn
}: IAuthAccountsBoxProps) {
  const { eventTrack } = useAnalyticsTracker();
  const toasterService = useToaster();

  const [deleteAccountConfirmationId, setDeleteAccountConfirmationId] = useState(null);
  const showConfirmationDialog = !!deleteAccountConfirmationId;
  const [selectedServiceAccountForEdit, setSelectedServiceAccountForEdit] =
    useState<AuthorizationAccount>(null);

  const showEditAccountDialog = !!selectedServiceAccountForEdit;

  useEffect(() => {
    if (authorisedAccounts.length && selectedAccountId) {
      const authorisedAccount = authorisedAccounts.find(acc => acc.id === selectedAccountId);

      onAccountChange(authorisedAccount?.id);
    }
  }, [authorisedAccounts]);

  const reAuthoriseAccount = (account: ServiceAccount | AuthorisedAccount) => {
    AuthAPI.reAuthoriseAccount(
      provider,
      account.id,
      window.location.href,
      window.location.href
    ).then(res => {
      if (res?.data?.consent_url) {
        window.location.assign(res.data.consent_url);
        return;
      }

      toasterService.pop('error', undefined, "Couldn't reauthorize this account");
    });
  };

  const editAccount = account => {
    setSelectedServiceAccountForEdit(account);
  };

  const deleteAccount = (account: AuthorizationAccount) => {
    setDeleteAccountConfirmationId(account.id);
  };

  const onAccountClick = (account: AuthorizationAccount) => {
    eventTrack({
      action: TRACKER_AUTH_ACCOUNT_CLICK,
      properties: {
        authType: account.getAccountType(),
        entityType
      }
    });

    onAccountChange(account.id);
  };

  const handleDeleteConfirmationEvent = () => {
    onDeleteAccount(deleteAccountConfirmationId);
    setDeleteAccountConfirmationId(null);
  };

  const [activeOptionsId, setActiveOptionsId] = useState<{ [id: string]: boolean }>({});

  const isOauthConnector = authType === ConnectorAuthTypeEnum.OAUTH;
  const isServiceConnector = authType === ConnectorAuthTypeEnum.SERVICE;

  return (
    <>
      <div className={styles.accountBox} data-id={generateDataId('')}>
        {authorisedAccounts.map((account, idx) => (
          <div
            key={idx}
            className={clsx('center-flex-row justify-between', {
              even: idx % 2 === 0,
              'border-bottom': idx !== authorisedAccounts.length - 1
            })}
          >
            <div
              className='center-flex-row flex-1 cursor-hand p-6 justify-between'
              data-id={generateDataId(`list-item-${idx}`)}
              onClick={() => onAccountClick(account)}
            >
              <div className='center-flex-row'>
                <HdRadioGroup name={`accountId-${idx}`} currentValue={`${selectedAccountId}`}>
                  <HdRadio
                    dataId={generateDataId(`list-item-${idx}`)}
                    onChange={() => {}}
                    value={`${account.id}`}
                  />
                </HdRadioGroup>

                <div className='text-medium ml-2'> {account.getDisplayName()} </div>

                {isOauthConnector &&
                  (account as AuthorisedAccount).environment &&
                  (account as AuthorisedAccount).environment !==
                    AuthAccountEnvironment.PRODUCTION && (
                    <div className='badge overlay-badge overlay-badge-medium ml-2' data-id={generateDataId(`isOauthConnectorBadge`)}>
                      {accountEnvironmentLabelFn((account as AuthorisedAccount).environment)}
                    </div>
                  )}
              </div>

              <div className='center-flex-row'>
                <span className='text-secondary'>
                  Added On{' '}
                  <HdTimeFormat
                    value={account.createdAt}
                    options={{
                      formats: 'DD/MM/YYYY'
                    }}
                  />
                </span>
              </div>
            </div>

            <div className='center-flex-row pr-5'>
              <HdIconButton
                dataId={generateDataId(`list-item-${idx}`)}
                id={`auth-list-item-${idx}`}
                onClick={() =>
                  setActiveOptionsId(options => ({
                    ...options,
                    [idx]: !options[idx]
                  }))
                }
              >
                <HdIcon name='more-vertical' />
              </HdIconButton>

              <HdMenu
                data-id={generateDataId(`list-item-${idx}-menu`)}
                open={!!activeOptionsId[idx]}
                onClose={() =>
                  setActiveOptionsId(options => ({
                    ...options,
                    [idx]: false
                  }))
                }
                target={`#auth-list-item-${idx}`}
              >
                {isOauthConnector && (
                  <HdMenuItem
                    data-id={generateDataId(`list-item-${idx}-reauthorize`)}
                    onClick={() => {
                      setActiveOptionsId(options => ({
                        ...options,
                        [idx]: false
                      }));
                      reAuthoriseAccount(account);
                    }}
                  >
                    <div className='center-flex-row'>
                      Reauthorize
                      {isProviderTypeGoogle && (
                        <>
                          {' '}
                          with <GoogleAuthIcon />
                        </>
                      )}
                    </div>
                  </HdMenuItem>
                )}

                {isServiceConnector && (
                  <HdMenuItem
                    data-id={generateDataId(`list-item-${idx}-edit`)}
                    onClick={() => {
                      setActiveOptionsId(options => ({
                        ...options,
                        [idx]: false
                      }));
                      editAccount(account);
                    }}
                  >
                    <span>Edit</span>
                  </HdMenuItem>
                )}

                <HdMenuItem
                  className='error has-separator'
                  data-id={generateDataId(`list-item-${idx}-delete`)}
                  onClick={() => {
                    setActiveOptionsId(options => ({
                      ...options,
                      [idx]: false
                    }));
                    deleteAccount(account);
                  }}
                >
                  <span>Delete</span>
                </HdMenuItem>
              </HdMenu>
            </div>
          </div>
        ))}

        <div className='border-top'>
          <NewAuthTrigger
            flatButton
            triggerLabel='Add another Account'
            onAuthClick={() => onNewAccountClick()}
          />
        </div>
      </div>

      <ConfirmDialogV1
        dataId={generateDataId('delete-account')}
        open={showConfirmationDialog}
        title='DELETE'
        modalProps={{ disableRestoreFocus: true }}
        body='Are you sure you want to delete this account?'
        positiveButtonText='Confirm'
        negativeButtonText='No'
        onClose={() => {
          setDeleteAccountConfirmationId(null);
        }}
        onPositiveClickCallback={handleDeleteConfirmationEvent}
      />

      <EditServiceAccountDialog
        closeModelHandler={() => {
          setSelectedServiceAccountForEdit(null);
        }}
        oldServiceAccount={selectedServiceAccountForEdit}
        open={showEditAccountDialog}
        provider={provider}
      />
    </>
  );
}

function GoogleAuthIcon() {
  return (
    <div className={clsx(styles.googleAuthContainer, 'ml-2')} data-id='google-auth-icon'>
      <div className={styles.googleIconContainer}>
        <img
          className={styles.googleIcon}
          src='https://res.cloudinary.com/hevo/image/upload/v1661186331/dashboard/google-icon_uyeuow.png'
          alt=''
        />
      </div>

      <span className='text-medium mr-3'>Google</span>
    </div>
  );
}
